/**
 * CopilotWriteConfirmationCard — carte « L'assistant propose : … » du tiroir du
 * copilote (UPT-2016, lot 2).
 *
 * Le copilote ne modifie jamais rien de lui-même : quand le modèle demande une
 * écriture, le serveur n'écrit RIEN et envoie une demande de confirmation. Cette
 * carte la montre en clair (résumé + charge utile « clé : valeur ») avec deux
 * boutons ; seul le clic sur « Confirmer » déclenche l'écriture, via une nouvelle
 * requête de flux portant `confirmWrite`. « Annuler » n'écrit rien.
 *
 * Une fois répondue, la carte n'offre plus de bouton (une demande ne se confirme
 * qu'une fois) ; l'issue arrive dans la conversation (CopilotWriteResultLine).
 */

import * as React from 'react';
import { useIntl } from 'react-intl';
import Button from '@/uptk/core/components/ui/Button';
import { payloadEntries } from '@/uptk/core/components/ai/proposals/aiProposalAdmin';
import type { WriteConfirmation, WriteResult, WriteResultTone } from './copilotWriteConfirmation';
import { writeResultLabelKey, writeResultTone } from './copilotWriteConfirmation';

/** Réponse déjà donnée à la carte ; null = en attente du clic. */
export type WriteConfirmationDecision = 'confirmed' | 'cancelled';

/** Valeur affichée pour un champ vide. */
const EMPTY_VALUE = '—';

type CardProps = {
  confirmation: WriteConfirmation;
  decision: WriteConfirmationDecision | null;
  /** Une requête est déjà en cours : pas de seconde réponse en parallèle. */
  busy: boolean;
  onAnswer: (requestId: string, confirmed: boolean) => void;
};

export function CopilotWriteConfirmationCard({ confirmation, decision, busy, onAnswer }: CardProps) {
  const intl = useIntl();
  const entries = React.useMemo(() => payloadEntries(confirmation.payload), [confirmation.payload]);
  const answered = decision !== null;

  return (
    <div
      role="group"
      aria-label={intl.formatMessage({ id: 'copilot.writeConfirmation.aria' })}
      className="mr-8 rounded-lg border border-portal-accent/40 bg-portal-accent/5 px-3 py-2 text-sm text-portal-ink"
    >
      <p className="font-medium">
        {intl.formatMessage({ id: 'copilot.writeConfirmation.title' }, { summary: confirmation.summary })}
      </p>

      {entries.length > 0 ? (
        <dl className="mt-2 max-h-40 overflow-y-auto rounded border border-portal-border bg-portal-bg px-2 py-1">
          {entries.map((entry, index) => (
            <div
              // eslint-disable-next-line react/no-array-index-key -- clés pointées uniques, l'index lève un doublon éventuel
              key={`${entry.key}-${index}`}
              className="grid grid-cols-3 gap-2 py-0.5"
            >
              <dt dir="ltr" className="break-all text-start font-mono text-xs text-portal-ink-muted">
                {entry.key}
              </dt>
              <dd dir="auto" className="col-span-2 min-w-0 whitespace-pre-wrap break-words text-xs">
                {entry.value === '' ? EMPTY_VALUE : entry.value}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}

      {answered ? (
        <p className="mt-2 text-xs italic text-portal-ink-muted">
          {intl.formatMessage({
            id: decision === 'confirmed'
              ? 'copilot.writeConfirmation.sent.confirmed'
              : 'copilot.writeConfirmation.sent.cancelled',
          })}
        </p>
      ) : (
        <>
          <p className="mt-2 text-xs text-portal-ink-muted">
            {intl.formatMessage({ id: 'copilot.writeConfirmation.hint' })}
          </p>
          <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
            <Button
              type="button"
              variant="themeOutline"
              size="sm"
              disabled={busy}
              onClick={() => onAnswer(confirmation.requestId, false)}
            >
              {intl.formatMessage({ id: 'copilot.writeConfirmation.cancel' })}
            </Button>
            <Button
              type="button"
              variant="cta"
              size="sm"
              disabled={busy}
              onClick={() => onAnswer(confirmation.requestId, true)}
            >
              {intl.formatMessage({ id: 'copilot.writeConfirmation.confirm' })}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

/** Classes par couleur d'état (jetons du design system, jamais la marque du locataire). */
const RESULT_TONE_CLASSES: Record<WriteResultTone, string> = {
  success: 'border-success-solid/40 bg-success-solid/10 text-success-text',
  danger: 'border-danger-solid/40 bg-danger-solid/10 text-danger-text',
  warning: 'border-warning-solid/40 bg-warning-solid/10 text-warning-text',
  neutral: 'border-portal-border bg-portal-surface-muted text-portal-ink-muted',
};

/** Issue d'une demande (EXECUTED / FAILED / CANCELLED / EXPIRED) dans la conversation. */
export function CopilotWriteResultLine({ result }: { result: WriteResult }) {
  const intl = useIntl();
  const tone = writeResultTone(result.status);
  return (
    <div
      role="status"
      className={`mr-8 rounded-lg border px-3 py-2 text-sm ${RESULT_TONE_CLASSES[tone]}`}
    >
      <p>{intl.formatMessage({ id: writeResultLabelKey(result.status) })}</p>
      {result.status === 'FAILED' && result.error !== null ? (
        <p className="mt-1 break-words text-xs">{result.error}</p>
      ) : null}
    </div>
  );
}
